import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, ChevronDown, ChevronRight, ChevronsDownUp, ChevronsUpDown, Loader2, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const LEVELS = [["building", "Building"], ["floor", "Floor"], ["area", "Area"], ["room", "Room"]];
const num = (n) => (Number.isInteger(n) ? n : Number(n).toFixed(1));
const errText = (e, fb) => { const d = e?.response?.data?.detail; return typeof d === "string" ? d : d?.message || fb; };

/** rows → location tree. Every node (location, scope group, line) carries its line list so one checkbox covers everything under it. */
function buildTree(rows) {
  const used = LEVELS.filter(([k]) => k === "room" || rows.some((r) => (r.room?.[k] || "").trim()));
  const loc = (r, k) => (k === "room" ? r.room?.room_key || "" : String(r.room?.[k] || "").trim());
  const label = (r, k) => (k === "room" ? `${r.room?.room_number || ""}${r.room?.room_name ? ` ${r.room.room_name}` : ""}`.trim() || "Room" : k === "floor" ? `Floor ${r.room.floor}` : k === "building" ? `Bldg ${r.room.building}` : r.room.area);
  const place = (r, upto) => ({ building: r.room?.building || "", floor: String(r.room?.floor || ""), area: upto >= 2 ? r.room?.area || "" : "", room_key: upto >= 3 ? r.room?.room_key || "" : "", room_label: upto >= 3 ? label(r, "room") : "" })
  const node = (key, level, text, p) => ({ key, level, label: text, loc: p, lines: [], children: [], scopes: new Map() });
  const root = node("root", "all", "All material", { building: "", floor: "", area: "", room_key: "", room_label: "" });
  rows.forEach((r) => {
    let cur = root; cur.lines.push(r);
    used.forEach(([k], i) => {
      const id = loc(r, k) || "—";
      const key = `${cur.key}/${k}:${id}`;
      let child = cur.children.find((c) => c.key === key);
      if (!child) { child = node(key, k, loc(r, k) ? label(r, k) : `No ${k}`, { ...place(r, i), ...(k === "building" ? { floor: "", area: "", room_key: "", room_label: "" } : k === "floor" ? { area: "", room_key: "", room_label: "" } : {}) }); cur.children.push(child); }
      child.lines.push(r); cur = child;
    });
  });
  const sortKids = (n) => { n.children.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })); n.children.forEach(sortKids); };
  sortKids(root);
  return { root, levels: used.map(([k]) => k) };
}
const sum = (lines, f) => lines.reduce((s, r) => s + (Number(r[f]) || 0), 0);
const stateOf = (lines) => {
  const total = sum(lines, "released"), placed = sum(lines, "delivered"), nd = sum(lines, "not_delivered"), open = sum(lines, "unaccounted");
  return { total, placed, nd, open, checked: total > 0 && open <= 0 && placed > 0 && nd <= 0, partial: placed > 0 && open > 0, issue: nd > 0 };
};
const scopesOf = (lines) => [...new Set(lines.map((r) => r.scope_type || "—"))].sort();
const sameScope = (sc) => (r) => (r.scope_type || "—") === sc;
/** The keys exactly as NodeRow renders them: a node under a scope row is keyed through that scope, so TC / Floor 1 and TRA / Floor 1 never collide. */
const scopeLevel = (n) => n.children.length > 0 && n.children[0].level === "room";   // scopes sit right above the rooms (Floor → TRA → Room → units)
function walkDisplay(n, key, visit) {
  visit({ kind: "node", key, level: n.level });
  if (scopeLevel(n)) {
    scopesOf(n.lines).forEach((sc) => {
      const pre = `${key}#${sc}`;
      visit({ kind: "scope", key: pre, level: n.level });
      n.children.filter((c) => c.lines.some(sameScope(sc))).forEach((c) => walkDisplay({ ...c, lines: c.lines.filter(sameScope(sc)) }, `${pre}/${c.key.split("/").pop()}`, visit));
    });
  } else n.children.forEach((c) => walkDisplay(c, `${key}/${c.key.split("/").pop()}`, visit));
}

/** Round box, same as the pull screen. One tap = deliver / plain uncheck · double-tap = missing unit. */
function Check({ st, onClick, onDouble, disabled, testId }) {
  const timer = React.useRef(null);
  const tap = (e) => {
    e.stopPropagation();
    if (timer.current) { clearTimeout(timer.current); timer.current = null; onDouble(); return; }
    timer.current = setTimeout(() => { timer.current = null; onClick(); }, 260);
  };
  return (
    <button type="button" onClick={tap} disabled={disabled} data-testid={testId} data-state={st.checked ? "checked" : st.partial ? "partial" : st.issue ? "issue" : "open"}
            aria-pressed={st.checked} title="Tap: delivered / undo · double-tap: missing unit"
            className={`h-11 w-11 min-h-[44px] min-w-[44px] sm:h-9 sm:w-9 sm:min-h-0 sm:min-w-0 !p-0 shrink-0 rounded-full border-2 inline-flex items-center justify-center text-sm font-bold ${st.checked ? "bg-emerald-600 border-emerald-600 text-white" : st.issue ? "border-red-500 text-red-600" : st.partial ? "border-emerald-500 text-emerald-700" : "border-border text-transparent"} disabled:opacity-40`}>
      {st.checked ? "✓" : st.issue ? "!" : st.partial ? "–" : "·"}
    </button>
  );
}

/** One location node: checkbox · label · units, then (expanded) its scopes, each with the child locations (or the product lines in a room). */
function LineRow({ r, d, busy, onCheck, onUncheck, onMissing }) {
  const lst = stateOf([r]);
  const last = r.placements?.length ? r.placements[r.placements.length - 1].location_label : "";
  return (
    <li className="flex items-center gap-2 py-1 pr-2 border-b border-border/40" style={{ paddingLeft: 8 + d * 14 + 36 }} data-testid={`dlv-line-${r.line_id}`}>
      <Check st={lst} disabled={busy} testId={`dlv-check-line-${r.line_id}`} onClick={() => (lst.checked ? onUncheck([r], r.product_short) : onCheck([r], r.product_short))} onDouble={() => onMissing([r], r.product_short)} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm text-foreground">{r.product_short} <span className="text-muted-foreground text-xs">{r.description}</span></span>
        <span className="block text-[11px] text-muted-foreground">{num(r.delivered)} / {num(r.released)} {r.uom}{r.not_delivered > 0 ? <span className="text-red-700 dark:text-red-300"> · {num(r.not_delivered)} missing</span> : null}{last ? ` · ${last}` : ""}</span>
      </span>
    </li>
  );
}

function ScopeRow({ n, sc, skey, depth, expanded, toggle, busy, onCheck, onUncheck, onMissing, nodeCheck, nodeUncheck, nodeMissing }) {
  const sl = n.lines.filter(sameScope(sc));
  const sst = stateOf(sl);
  const sopen = expanded.has(skey);
  const kids = n.children.filter((c) => c.lines.some(sameScope(sc))).map((c) => ({ ...c, lines: c.lines.filter(sameScope(sc)) }));
  const label = `${sc} · ${n.label}`;
  return (
    <li data-testid={`dlv-scope-${skey}`}>
      <div className="flex items-center gap-2 py-1 pr-2 border-b border-border/40 bg-background" style={{ paddingLeft: 8 + (depth + 1) * 14 }}>
        <button type="button" onClick={() => toggle(skey)} className="grid h-7 w-7 place-items-center rounded text-muted-foreground hover:bg-muted" data-testid={`dlv-toggle-${skey}`}>{sopen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}</button>
        <Check st={sst} disabled={busy} testId={`dlv-check-${skey}`} onClick={() => (sst.checked ? onUncheck(sl, label) : onCheck(sl, label))} onDouble={() => onMissing(sl, label)} />
        <button type="button" onClick={() => toggle(skey)} className="min-w-0 flex-1 text-left">
          <span className="block text-xs font-semibold uppercase tracking-wide text-foreground">{sc}</span>
          <span className="block text-[11px] text-muted-foreground">{num(sst.placed)} / {num(sst.total)} · {kids.length} {kids.length === 1 ? "room" : "rooms"}</span>
        </button>
      </div>
      {sopen && <ul>{kids.map((c) => <NodeRow key={c.key} n={c} nodeKey={`${skey}/${c.key.split("/").pop()}`} depth={depth + 2} expanded={expanded} toggle={toggle} busy={busy} onCheck={nodeCheck} onUncheck={nodeUncheck} onMissing={nodeMissing} />)}</ul>}
    </li>
  );
}

function NodeBody({ n, nodeKey, depth, expanded, toggle, busy, onCheck, onUncheck, onMissing }) {
  const sub = (lines, label) => ({ ...n, lines, label });
  if (scopeLevel(n)) {
    return <ul>{scopesOf(n.lines).map((sc) => <ScopeRow key={sc} n={n} sc={sc} skey={`${nodeKey}#${sc}`} depth={depth} expanded={expanded} toggle={toggle} busy={busy}
                                                       onCheck={(l, t) => onCheck(sub(l, t))} onUncheck={(l, t) => onUncheck(sub(l, t))} onMissing={(l, t) => onMissing(sub(l, t))}
                                                       nodeCheck={onCheck} nodeUncheck={onUncheck} nodeMissing={onMissing} />)}</ul>;
  }
  if (n.children.length) {
    return <ul>{n.children.map((c) => <NodeRow key={c.key} n={c} nodeKey={`${nodeKey}/${c.key.split("/").pop()}`} depth={depth + 1} expanded={expanded} toggle={toggle} busy={busy} onCheck={onCheck} onUncheck={onUncheck} onMissing={onMissing} />)}</ul>;
  }
  return <ul>{n.lines.map((r) => <LineRow key={r.line_id} r={r} d={depth + 1} busy={busy} onCheck={(l, t) => onCheck(sub(l, t))} onUncheck={(l, t) => onUncheck(sub(l, t))} onMissing={(l, t) => onMissing(sub(l, t))} />)}</ul>;
}

/** One location node: round box · label · units. Floors carry the scope rows (TRA, TC…); rooms carry the product lines. */
function NodeRow(props) {
  const { n, nodeKey, depth, expanded, toggle, busy, onCheck, onUncheck, onMissing } = props;
  const st = stateOf(n.lines);
  const open = expanded.has(nodeKey);
  return (
    <li data-testid={`dlv-node-${nodeKey}`} data-level={n.level}>
      <div className={`flex items-center gap-2 py-1.5 pr-2 border-b border-border/60 ${depth === 0 ? "bg-muted/30" : ""}`} style={{ paddingLeft: 8 + depth * 14 }}>
        <button type="button" onClick={() => toggle(nodeKey)} className="grid h-7 w-7 place-items-center rounded text-muted-foreground hover:bg-muted" data-testid={`dlv-toggle-${nodeKey}`} aria-label={open ? "Collapse" : "Expand"}>
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <Check st={st} disabled={busy} testId={`dlv-check-${nodeKey}`} onClick={() => (st.checked ? onUncheck(n) : onCheck(n))} onDouble={() => onMissing(n)} />
        <button type="button" onClick={() => toggle(nodeKey)} className="min-w-0 flex-1 text-left">
          <span className="block truncate text-sm font-medium text-foreground">{n.label}</span>
          <span className="block text-[11px] text-muted-foreground">{num(st.placed)} / {num(st.total)} delivered{st.nd > 0 ? <span className="text-red-700 dark:text-red-300"> · {num(st.nd)} not delivered</span> : null} · {scopesOf(n.lines).join(", ")}</span>
        </button>
      </div>
      {open ? <NodeBody {...props} /> : null}
    </li>
  );
}

/** The delivery panel — check material off down the FRS hierarchy. Bulk opens at the top level, Spread down to the floor. */
export default function DeliveryPanel({ ticketId, row, actor, wide, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [busy, setBusy] = useState(false);
  const [missing, setMissing] = useState(null);     // { node, note }
  const load = () => api.get(`/delivery/tickets/${ticketId}`).then(({ data: d }) => setData(d)).catch((e) => { toast.error(errText(e, "Could not open the delivery")); onClose(); });
  useEffect(() => { load(); }, [ticketId]);          // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const f = (e) => { if (e.key === "Escape" && !document.querySelector('[role="dialog"]')) onClose(); }; window.addEventListener("keydown", f); return () => window.removeEventListener("keydown", f); }, [onClose]);
  const tree = useMemo(() => (data ? buildTree(data.rows || []) : null), [data]);
  const type = row?.delivery_type || (data?.ticket?.delivery_type) || "spread";
  const allKeys = useMemo(() => {
    if (!tree) return [];
    const out = [];
    walkDisplay(tree.root, "root", (v) => out.push(v.key));
    return out;
  }, [tree]);
  useEffect(() => {
    if (!tree || expanded !== null) return;
    // default view — Bulk: the highest level only · Spread: open down to the floor (scopes under each)
    const open = new Set(["root"]);
    if (type === "spread") walkDisplay(tree.root, "root", (v) => { if (v.kind === "node" && (v.level === "all" || v.level === "building")) open.add(v.key); });
    setExpanded(open);
  }, [tree, type, expanded]);
  const toggle = (k) => setExpanded((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const allOpen = expanded && allKeys.every((k) => expanded.has(k));
  const after = async (msg) => { toast.success(msg); await load(); onChanged?.(); };
  const check = async (n) => {
    const ids = n.lines.filter((r) => r.unaccounted > 0 || r.not_delivered > 0).map((r) => r.line_id);   // a missing unit that turned up re-checks too
    if (!ids.length) { toast.info("Nothing under here is still to deliver"); return; }
    if (type === "bulk" && n.level === "all") { toast.info("Bulk drops need a level — check the building, floor or area it was left at"); return; }
    setBusy(true);
    try { const { data: res } = await api.post(`/delivery/tickets/${ticketId}/deliver`, { line_ids: ids, node: n.loc, by: actor }); await after(`${num(res.placed)} unit(s) delivered · ${n.label}${res.delivery_stage?.startsWith("Delivered") ? " — delivery complete" : ""}`); }
    catch (e) { toast.error(errText(e, "Could not mark it delivered")); }
    finally { setBusy(false); }
  };
  const uncheck = async (n) => {
    const ids = n.lines.filter((r) => r.delivered > 0).map((r) => r.line_id);
    if (!ids.length) return;
    setBusy(true);
    try { const { data: res } = await api.post(`/delivery/tickets/${ticketId}/undeliver`, { line_ids: ids, by: actor }); await after(`${num(res.unplaced)} unit(s) unchecked · ${n.label}`); }
    catch (e) { toast.error(errText(e, "Could not uncheck it")); }
    finally { setBusy(false); }
  };
  const markMissing = (n) => setMissing({ node: n, note: "" });
  const confirmMissing = async () => {
    const ids = missing.node.lines.map((r) => r.line_id);
    setBusy(true);
    try { const { data: res } = await api.post(`/delivery/tickets/${ticketId}/missing`, { line_ids: ids, note: missing.note, by: actor }); setMissing(null); await after(`Missing unit — ${num(res.not_delivered)} unit(s) · warehouse and foreman notified`); }
    catch (e) { toast.error(errText(e, "Could not record the missing unit")); }
    finally { setBusy(false); }
  };
  const t = data?.ticket || {};
  const st = tree ? stateOf(tree.root.lines) : null;
  return (
    <div className={wide ? "h-full min-h-0 flex flex-col" : "fixed inset-0 z-50 !mt-0 bg-background flex flex-col frs-field-shell"} data-testid="delivery-panel">
      <header className="shrink-0 border-b border-border bg-card px-3 py-2 flex flex-wrap items-center gap-2">
        {!wide && <button type="button" onClick={onClose} className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center rounded-full text-foreground" data-testid="dlv-back" aria-label="Back"><ArrowLeft className="w-5 h-5" /></button>}
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-foreground truncate" data-testid="dlv-title">{[t.job_number, t.project_name].filter(Boolean).join(" · ") || "Delivery"}</div>
          <div className="text-[11px] text-muted-foreground flex flex-wrap items-center gap-1.5">
            <span className="font-mono">{t.ticket_number}</span>
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${type === "bulk" ? "bg-sky-100 text-sky-900" : "bg-violet-100 text-violet-900"}`} data-testid="dlv-type">{type === "bulk" ? "Bulk" : "Spread"}</span>
            {st && <span data-testid="dlv-progress"><b className="text-foreground">{num(st.placed)}</b> / {num(st.total)} delivered{st.nd > 0 ? <span className="text-red-700"> · {num(st.nd)} missing</span> : null}</span>}
            {t.address ? <span className="inline-flex items-center gap-1 truncate"><MapPin className="h-3 w-3" />{t.address}</span> : null}
            {t.foreman?.name ? <span>· {t.foreman.name}</span> : null}
          </div>
        </div>
        <button type="button" onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground" data-testid="dlv-close" aria-label="Close" title="Close (Esc)"><X className="h-4 w-4" /></button>
      </header>
      <div className="flex-1 min-h-0 overflow-auto">
        {!data || !expanded ? <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div> : (
          <>
            <div className="px-3 py-2 flex items-start gap-3 border-b border-border bg-muted/20">
              <button type="button" onClick={() => setExpanded(new Set(allOpen ? ["root"] : allKeys))} data-testid="dlv-expand-all" title={allOpen ? "Collapse all" : "Expand all"}
                      className="min-h-[36px] shrink-0 inline-flex items-center gap-1.5 px-3 rounded-full border border-border text-xs font-medium text-foreground bg-card hover:bg-muted">
                {allOpen ? <ChevronsDownUp className="h-3.5 w-3.5" /> : <ChevronsUpDown className="h-3.5 w-3.5" />}{allOpen ? "Collapse all" : "Expand all"}
              </button>
              <p className="text-[11px] text-muted-foreground" data-testid="dlv-hint">
                {type === "bulk" ? "Bulk: tap the level it was all left at — every room under it checks too." : "Spread: tap a floor, scope or room once it is in place — everything under it checks too."} Tap again to uncheck. <b>Double-tap</b> a box to flag a missing unit (warehouse + foreman told).
              </p>
            </div>
            <ul data-testid="dlv-tree"><NodeRow n={tree.root} nodeKey="root" depth={0} expanded={expanded} toggle={toggle} onCheck={check} onUncheck={uncheck} onMissing={markMissing} busy={busy} /></ul>
          </>
        )}
      </div>
      <Dialog open={!!missing} onOpenChange={(o) => !o && setMissing(null)}>
        <DialogContent className="max-w-sm" data-testid="dlv-missing-dialog">
          <DialogHeader>
            <DialogTitle>Missing unit — {missing?.node?.label}</DialogTitle>
            <DialogDescription>{missing ? `${num(stateOf(missing.node.lines).placed + stateOf(missing.node.lines).open)} unit(s) are recorded Not delivered. The warehouse and the foreman get a "missing unit" to investigate.` : ""}</DialogDescription>
          </DialogHeader>
          <Textarea autoFocus value={missing?.note || ""} onChange={(e) => setMissing({ ...missing, note: e.target.value })} placeholder="What's missing / where you looked (optional)" data-testid="dlv-missing-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setMissing(null)} data-testid="dlv-missing-cancel">Cancel</Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white" disabled={busy} onClick={confirmMissing} data-testid="dlv-missing-confirm">Mark missing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
